import { inject, Injectable } from '@angular/core';
import { HelperService } from '@common/services/helper.service';
import { HelperTimeService } from '@common/services/helper-time.service';
import { EventNeoBuild, EventNotifications, Fichado, Note, FichaAlert, FichaEvent2Html, EventTypeData, FichaSettings, FichaStates, ScheduleLabels, HtmlEventTypeId, EventNeoTyped } from '../interfaces/ficha-event.interface';
import { FichaTimeControlService } from './ficha-time-control.service';
import { MetaNeo } from '@fichaje/interfaces/ficha.interface';
import { ApiRouteService } from '@common/api';

@Injectable({
  providedIn: 'root'
})
export class FichaEventBuildService {
  private readonly helpData =       inject(HelperService)
  private readonly helpTime =       inject(HelperTimeService)
  private readonly fichaTimeControl = inject(FichaTimeControlService)
  private readonly apiRoute =        inject(ApiRouteService)


  //? ### 5) - build 'scheduleEvents' = SETTINGS, STATES y 'eventsRaw' ########
  public buildEvents2Html(
    evTyped:    EventNeoTyped[] | undefined,
    settings:   FichaSettings | undefined,
    states:     FichaStates | undefined,
    metaNeo:    MetaNeo | undefined,
  ): FichaEvent2Html[]{

    const events2Html:FichaEvent2Html[] = [];
    if ( !(evTyped && evTyped.length > 0 && settings && states) ) {
      return events2Html
    }

    for (const ev of evTyped) {
      const showGpsMap = settings.userLogged.includeLocation && settings.whenIsIt === 'today';
      const requireLocation = settings.userLogged.mapa_requerido_fichar && settings.whenIsIt === 'today';
        const eventTypeData = { ...ev.eventTypeData };
        if (metaNeo?.meta_v2?.opcionales?.available) {
          const opt = metaNeo.meta_v2.opcionales.available.find(o => o.horario_id === ev.schedule_rule_id);
          if (opt?.color) {
            eventTypeData.borderColor = opt.color;
          }
        }

        const event: FichaEvent2Html = {
        hasExpected:  true, // this.setHasExpected(ev), //! puede que sea en Type
        scheduleId:   typeof ev.id === 'number' ? ev.id : 0,
        eventId:      'id'+ev.id,
        companyId:    ev.company_id,
        workerId:     settings.workerCurrent?.worker_id ?? settings.userLogged.id,
        eventType:    eventTypeData,

        hasMap:       !!( this.resolveStartMap(ev) || showGpsMap || this.resolveEndMap(ev) ) && !settings.isStation,
        start:        this.setEventStart(ev, settings, showGpsMap, requireLocation), //this.setStart(eventR, settings, eventType),   //
        sub_events:   this.setSubEvents(ev),
        end:          this.setEventEnd(ev, settings, showGpsMap, requireLocation),
        workingHours: this.setWorkingHours(ev, settings),

        hasSummary:   false,
        permisosFinalizados:  ev.meta_closed_leaves ?? undefined,
        pausasFinalizadas:    ev.meta_closed_pause ?? undefined,

        clients:     this.setClients(ev, settings),
        teletrabajo: this.setTeletrabajo(ev, settings),
        signature:   this.setSignature(ev, settings),

        tipoIncidencia: ev.incidence_code,

        instructions:  ev.instructions_by_management ?? undefined,
        incidencias:   this.setIncidencias(ev, $localize`:@@eventNotifications.incidentsTitle:Incidencias`),
        metaNeo:        metaNeo,
        openSubEventsNotifications: this.setOpenSubEvents(ev, states),
        openOvertime: ev.meta_open_sub_event,

        eventInProgressActionButton: states.eventInProgressActionButton ?? 'none',

        timeControl: this.fichaTimeControl.setTimeControl(ev, settings),
        settings: settings,
        isEventPending : !ev.event_end_clock_out,
        isInProgress : !!ev.event_start_clock_in && !ev.event_end_clock_out,
      }
      event.hasSummary = this.setHasSummary(event)
      events2Html.push(event);
    }

    return events2Html
  }

  //? #########################################################################
  //? ### SET PARAMETER FUNCTIONS #############################################


  private setHasSummary(event:FichaEvent2Html):boolean{
    const summary =
      event.end.real !== undefined   ||
      !!event.sub_events?.pauses     ||
      !!event.sub_events?.permission ||
      !!event.sub_events?.lunch      ||
      !!event.permisosFinalizados    ||
      !!event.signature?.firma       ||
      !!event.pausasFinalizadas      ||
      event.teletrabajo.showInInterface

    return summary
  }

  private setSubEvents( eventR:EventNeoBuild ) {
    const permisosFin = eventR.meta_closed_leaves;
    let pausaDisponibleInSeconds = 0;
    let sumaDePausasTotalInSeconds = 0;
    const pausasSecondsPretty = () => {
      const pausasFin = eventR.meta_closed_pause;
      const pausasFinalizadasInSeconds = pausasFin ? this.helpData.totalizeArrayObjs(pausasFin, 'elapsed_seconds') : 0;
      pausaDisponibleInSeconds = eventR.pauses_limit? eventR.pauses_limit*60 : 0;
      sumaDePausasTotalInSeconds = pausaDisponibleInSeconds - pausasFinalizadasInSeconds;
      const tipo = sumaDePausasTotalInSeconds >= 0 ? '' : ''; // sumaDePausasTotalInSeconds >= 0 ? ' +' : ''; //! regresar la version comentada que es la correcta
      return eventR.pauses_limit || pausasFinalizadasInSeconds ?
              this.helpTime.secondsToTimeSpanPretty(Math.abs(sumaDePausasTotalInSeconds)) + tipo :
              undefined;
    }
    const subEvents = {
      lunch: eventR.lunch ? this.helpTime.secondsToTimeSpanPretty(eventR.lunch*60) : undefined,
      pauses: pausasSecondsPretty(),
      nonComputablePauses: sumaDePausasTotalInSeconds < 0 ? this.helpTime.secondsToTimeSpanPretty(Math.abs(pausaDisponibleInSeconds)) : undefined,
      permission: permisosFin ?
              this.helpTime.secondsToTimeSpanPretty(
              this.helpData.totalizeArrayObjs(permisosFin, 'elapsed_minutes') * 60
              ) :
              undefined,
    }

    return subEvents
  }

  private setClients( eventR: EventNeoBuild, settings: FichaSettings,){
    let clients = undefined;
    if(settings.userLogged.clientesFichaje) //allowClients
      clients = {
        id:eventR.client_assign_id ?? 0,
        name:eventR.client_assign_name ?? ''
      }
    return clients;
  }

  private setTeletrabajo( eventR: EventNeoBuild, settings: FichaSettings ){
    const capabilities = settings.capabilities;
    // type=´work´ && is_station=false && readOnly = false && teletrabajo_activo = true &&
    // teletraba_dia = true && informe_firmado = false && (es_hoy = true || es_futuro = true)
    return {
      isSelected:      eventR.remote_work,
      showInInterface: !!capabilities?.allow_remote_work && !capabilities?.is_informe_firmado &&
                       eventR.type == 'work' &&
                       !settings.isStation && !settings.readOnly &&
                       (settings.whenIsIt === 'today' || settings.whenIsIt ===  'future')
    };
  }

  private setSignature(eventR:EventNeoBuild, settings: FichaSettings ){
    const capabilities = settings.capabilities;
    return {
      firma: eventR.signature_img ?? undefined,
      firma_requerida: !!settings.userLogged.firma_requerida,
      informe_firmado: !!capabilities?.is_informe_firmado,
    }
  }

  private setIncidencias(eventR:EventNeoBuild, title:string){
    const notes:Note[] = [];
    const setItem = (label:string, motivo:string|null, comentario:string|null) => {
      let description = motivo && comentario ? motivo + ' - ' : motivo ?? '';
      description += comentario ?? ''
      return { label:label, description:description }
    }
    if (eventR.incidence_clock_in_id)
      notes.push(
        setItem($localize`:@@eventNotifications.entryLabel:Entrada`, eventR.incidence_clock_in_reason, eventR.incidence_clock_in_comment)
      );
    if (eventR.incidence_clock_out_id)
      notes.push(
        setItem($localize`:@@eventNotifications.exitLabel:Salida`, eventR.incidence_clock_out_reason, eventR.incidence_clock_out_comment)
      );
    if (eventR.incidence_working_hours_id)
      notes.push(
        setItem($localize`:@@eventNotifications.workdayLabel:Jornada`, eventR.incidence_working_hours_reason, eventR.incidence_working_hours_comment)
      );
    return notes.length > 0 ? {title:title, notes:notes} as EventNotifications : undefined;
  }

  private setOpenSubEvents(eventR:EventNeoBuild, states:FichaStates):EventNotifications[]|undefined{

    const eventId = eventR.id
    const hExtraOpen  = states.meta?.horasExtrasActivas;
    const permisoOpen = states.meta?.permisosDelDiaActivos;
    const pausaOpen   = states.meta?.pausasActivas;

    const build:EventNotifications[] = [];
    if(hExtraOpen?.schedule_id == eventId)
      build.push( {
        title:$localize`:@@eventNotifications.openOvertimeTitle:Hora Extra Abierta`,
        notes:[
          { label: hExtraOpen.comment ?? $localize`:@@eventNotifications.overtimeLabel:Hora Extra`,
            description: this.helpTime.ISO8601toLocalTimePretty( hExtraOpen.date_in, 'hh:mm') ?? '--:--' }
        ]
    } );
    if(permisoOpen && +permisoOpen.schedule_id == eventId)
      build.push( {
        title:$localize`:@@eventNotifications.openPermissionTitle:Permiso Abierto`,
        notes:[
          { label: permisoOpen.motivo_request + (permisoOpen.comment ? ' - ' + permisoOpen.comment : ''),
            description: this.helpTime.ISO8601toLocalTimePretty( permisoOpen.date_in, 'hh:mm') ?? '--:--'  }]
      } );
    if(pausaOpen && +pausaOpen.schedule_id == eventId)
      build.push( {
        title:$localize`:@@eventNotifications.openBreakTitle:Pausa Abierta`,
        notes:[
          { label: pausaOpen.motivo_request + (pausaOpen.comment ? ' - ' + pausaOpen.comment : ''),
            description: this.helpTime.ISO8601toLocalTimePretty(pausaOpen.date_in, 'hh:mm:ss') ?? '--:--'  }]
      } );

    return build.length > 0 ? build : undefined;
  }

  // #########################################################################
  // ### HELPER FUNCTIONS ####################################################

  private resolveStartMap(ev: EventNeoBuild): string | undefined {
    return this.resolveEventMap(ev.event_start_location_map_img, ev.event_start_location_latitud, ev.event_start_location_longitud);
  }

  private resolveEndMap(ev: EventNeoBuild): string | undefined {
    return this.resolveEventMap(ev.event_end_location_map_img, ev.event_end_location_latitude, ev.event_end_location_longitude);
  }

  /**
   * El backend devuelve el `mapa_in`/`mapa_out` guardado en BD tal cual. Los fichajes antiguos
   * guardaron un `blob:` del navegador (caduca con la sesión y no se puede pedir por HTTP):
   * se regenera desde las coordenadas con el source `fichaje-evento`, como hace el frontend legacy.
   */
  private resolveEventMap(map: string | null | undefined, lat: number | null | undefined, lng: number | null | undefined): string | undefined {
    if (!map) return undefined;
    if (!map.startsWith('blob:')) return map;
    if (lat == null || lng == null) return undefined;
    try {
      return this.apiRoute.calendarMapUrl('fichaje-evento', lat, lng);
    } catch {
      return undefined;
    }
  }

  private setGpsMapImg(eventR:EventNeoBuild, settings: FichaSettings,){
    let map = undefined;
    // Si el usuario tiene mapas activos && es hoy && aun no se ha fichado la entrada = url a mapa
    if(settings.userLogged.includeLocation &&
      settings.whenIsIt === 'today' &&
      !eventR.event_start_clock_in
    )
    return map;
  }


  private setEventStart(
    ev:EventNeoTyped,
    settings: FichaSettings,
    showGpsMap:boolean,
    requireLocation:boolean
    ):Fichado
  {
    const type = ev.eventTypeData;
    let shouldCompare = type.showExpectedStart;
    if(ev.event_start_expected == ev.event_end_expected) shouldCompare = false;
    //! evaluar si es necesario validar si isFuture
    const colors =   settings.kpiColors;
    const real =     ev.event_start_clock_in ?? undefined;
    const expected = ev.event_start_expected;
    const locMap =   this.resolveStartMap(ev);
    const locStation = ev.event_start_location_prone_name ?? undefined;

    const expectedPretty = shouldCompare && expected ? this.helpTime.ISO8601toLocalTimePretty(expected) : undefined;

    const setColor = () => {
      let color;
      if (!ev.event_start_clock_in) color = colors.none;
      else if (!type.showExpectedStart) return type.borderColor;
      else if (!(expected || !real) || !shouldCompare) color = colors.none;


      else color = ev.incidence_clock_in_id == null ? colors.good : colors.bad;
      // else if (ev.incidence_code == 0 && !ev.event_start_clock_in) color = colors.none;
      // else color = [1, 5, 6, 7, 8, 10].includes(ev.incidence_code) ? colors.bad : colors.good;
      return color
    }; // 1 5,6,7,8 entrada.... lo demas salida

    return {
      real: [8, 10].includes(ev.incidence_code) ? 'SF' : this.helpTime.ISO8601toLocalTimePretty(real??undefined),
      expected: expectedPretty,
      color: setColor(),
      location: {
        map:locMap,
        station: locStation,
        showGpsMap: (showGpsMap && !ev.event_start_clock_in) ?? false,
        requireLocation:  (requireLocation && !ev.event_start_clock_in) ?? false,
      },
    }
  }
  private setEventEnd(
    ev:EventNeoTyped,
    settings: FichaSettings,
    showGpsMap:boolean,
    requireLocation:boolean
    ):Fichado
  {
    const type = ev.eventTypeData;
    let shouldCompare = type.showExpectedEnd;
    if(ev.event_start_expected == ev.event_end_expected) shouldCompare = false;
    //! evaluar si es necesario validar si isFuture
    const colors =   settings.kpiColors;
    const real =     ev.event_end_clock_out ?? undefined;
    const expected = ev.event_end_expected;
    const locMap =   this.resolveEndMap(ev);
    const locStation = ev.event_end_location_prone_name ?? undefined;

    const expectedPretty = shouldCompare && expected ? this.helpTime.ISO8601toLocalTimePretty(expected) : undefined;

    const setColor = () => {
      let color;
      if (!ev.event_end_clock_out) color = colors.none;

      else if (!type.showExpectedStart) return type.borderColor;

      else if (!(expected || !real) || !shouldCompare) color = colors.none;
      else if (!ev.event_end_clock_out) color = colors.none;
      else if (ev.incidence_code == 0 ) color = colors.good;

      else color = [2, 3, 4, 5, 6, 7, 8, 9, 10].includes(ev.incidence_code) ? colors.bad : colors.good;
      return color
    }; // 1 5,6,7,8 entrada.... lo demas salida

    return {
      real: [8, 9].includes(ev.incidence_code) ? 'SF' : this.helpTime.ISO8601toLocalTimePretty(real??undefined),
      expected: expectedPretty,
      color: setColor(),
      location: {
        map:locMap,
        station: locStation,
        showGpsMap: (showGpsMap && !!ev.event_end_clock_out) ?? false,
        requireLocation: (requireLocation && !ev.event_end_clock_out) ?? false,
      },
    }
  }

  private setWorkingHours( ev: EventNeoTyped, settings: FichaSettings ): Fichado
  {
    const capabilities = settings.capabilities;
    // segundos de pausa para sumar o restar a la jornada
    const pausasFinArrObj = ev.meta_closed_pause;
    const pausasFinalizadasInSeconds = pausasFinArrObj ? this.helpData.totalizeArrayObjs(pausasFinArrObj, 'elapsed_seconds') : 0;
    const pausaDisponibleInSeconds = ev.pauses_limit ? ev.pauses_limit*60 : 0;
    const sumaDePausasTotalInSeconds = pausaDisponibleInSeconds - pausasFinalizadasInSeconds;

    //! AVeriguar si para converiones de segundos a minutos, 110 segundos = 1 minuto o 2 minutos ?
    const pausasNoUsadasSumaJornada = !!capabilities?.pauses_computable; // bartell = false, auser = true
    const pausasEnSecsNoUsadas = sumaDePausasTotalInSeconds > 0 && !pausasNoUsadasSumaJornada ? sumaDePausasTotalInSeconds : 0

    const isFuture = settings.whenIsIt === 'future';
    const colors = settings.kpiColors;
    const realBase = ev.working_hours_clocked;
    const expected = ev.working_hours_expected;
    const realCalculadaSecs = true && realBase && realBase > 0 ? realBase*60 + sumaDePausasTotalInSeconds - pausasEnSecsNoUsadas : undefined;

    const realPretty =     realCalculadaSecs ? this.helpTime.secondsToTimeSpanPretty(realCalculadaSecs, false) : undefined;
    // const realPretty =     realBase ? this.helpTime.secondsToTimeSpanPretty(realBase*60, false) : undefined;
    const expectedPretty = expected ? this.helpTime.secondsToTimeSpanPretty(expected*60) : '';
    const shouldCompare =  !isFuture && expected ;
    // const deviationInMinutes = (realBase ?? 0) - (expected ?? 0);

    const deviationInSecs = realCalculadaSecs &&  expected ? realCalculadaSecs - expected*60 : 0; //(realCalculadaSecs ?? 0) - (expected*60 ?? 0);

    const setColorNew = () => {
      let color:string|undefined= colors.none;
      // if ( !type.showKpiColors ) color = colors.none;
      // else
      if (!ev.event_end_clock_out) color = colors.none;

      else if (!ev.eventTypeData.showExpectedStart) return ev.eventTypeData.borderColor;

      else if (!(expected || !realBase) || !shouldCompare) color = colors.none;

      else if (ev.incidence_code == 0 ) color = colors.good;

      else if (ev.working_hours_tolerance = -1) return colors.good;
      else if (deviationInSecs >= 0) return colors.good;
      else {
        const desviation = deviationInSecs/60
        color = (ev.working_hours_tolerance + desviation) > 0 ? colors.bad : colors.good
      }

      return color
    }
    return {
      real:  ev.incidence_code === 9 ? 'SF' : realPretty,
      expected: expectedPretty,
      difference: ev.incidence_code === 9 ?
            undefined :
            (deviationInSecs < 0 ? '- ' : '') + this.helpTime.secondsToTimeSpanPretty(Math.abs(deviationInSecs)),
      color: setColorNew()
    }
  }
}
